import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChatSessionContext } from '@/munshi/types';
import { upsertChatSessionContext } from '@/munshi/services/munshiService';
import { createAsyncThunk } from '@reduxjs/toolkit';

// State: holds a single ChatSessionContext (no mapping)
interface ChatSessionContextState {
  context: ChatSessionContext | undefined;
  pendingChanges: number;
  totalChanges: number;
}

const initialState: ChatSessionContextState = {
  context: undefined,
  pendingChanges: 0,
  totalChanges: 0,
};

const chatSessionContextSlice = createSlice({
  name: 'chatSessionContext',
  initialState,
  reducers: {
    // Set/replace the ChatSessionContext
    setContext(state, action: PayloadAction<ChatSessionContext>) {
      state.context = action.payload;
      state.pendingChanges += 1;
      state.totalChanges += 1;
    },
    // Patch/update the ChatSessionContext (shallow merge)
    patchContext(state, action: PayloadAction<Partial<ChatSessionContext>>) {
      if (state.context) {
        state.context = { ...state.context, ...action.payload };
        state.pendingChanges += 1;
        state.totalChanges += 1;
      }
    },
    // Clear the ChatSessionContext
    clearContext(state) {
      state.context = undefined;
      state.pendingChanges = 0;
      state.totalChanges = 0;
    },
    // Reset pendingChanges counter
    resetPendingChanges(state) {
      state.pendingChanges = 0;
    },
  },
});

// Selector
export const selectChatSessionContext = (state: { chatSessionContext?: ChatSessionContextState }) => state.chatSessionContext?.context;

export const {
  setContext,
  patchContext,
  clearContext,
  resetPendingChanges,
} = chatSessionContextSlice.actions;

// Thunks for async Supabase persistence with batching
export const setContextAsync = createAsyncThunk(
  'chatSessionContext/setContextAsync',
  async (context: ChatSessionContext, { dispatch, getState }) => {
    dispatch(setContext(context));
    const state = getState() as { chatSessionContext?: ChatSessionContextState };
    const pending = state.chatSessionContext?.pendingChanges || 0;
    if (pending >= 5) {
      await upsertChatSessionContext(context);
      dispatch(resetPendingChanges());
    }
  }
);

export const patchContextAsync = createAsyncThunk(
  'chatSessionContext/patchContextAsync',
  async (patch: Partial<ChatSessionContext> & { forceNoSupabaseUpdate?: boolean }, { getState, dispatch }) => {
    const { forceNoSupabaseUpdate, ...contextPatch } = patch;
    dispatch(patchContext(contextPatch));
    if (forceNoSupabaseUpdate) return;
    const state = getState() as { chatSessionContext?: ChatSessionContextState };
    const current = state.chatSessionContext?.context;
    if (current) {
      upsertChatSessionContext(current);
      dispatch(resetPendingChanges());
    }
  }
);

// Force flush: persist context and reset counter
export const flushContextAsync = createAsyncThunk(
  'chatSessionContext/flushContextAsync',
  async (_, { getState, dispatch }) => {
    const state = getState() as { chatSessionContext?: ChatSessionContextState };
    const current = state.chatSessionContext?.context;
    if (current) {
      await upsertChatSessionContext(current);
      dispatch(resetPendingChanges());
    }
  }
);

export default chatSessionContextSlice.reducer; 